import React, { useState, useEffect, useMemo } from "react";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";

import {
  CvEditorSession,
  CvEditorSessionStatus,
  CvMessage,
  CvMessageActor,
  UserInputType,
} from "../../types/cv-editor.types";
import {
  moveToNextInterviewStep,
  sendInterviewAudioReply,
  sendInterviewSkipReply,
  sendInterviewTextReply,
} from "../../../../../../../../services/cv-editor.service";
import { ChatHistory } from "./ChatHistory";
import { InputArea } from "./InputArea";
import { AudioUsageTracker } from "./AudioUsageTracker";

interface InterviewStepProps {
  session: CvEditorSession;

  dispatch: React.Dispatch<any>;
}

export const InterviewStep: React.FC<InterviewStepProps> = ({
  session,
  dispatch,
}) => {
  const { t } = useTranslation("cvEditor");

  // Determine the current interview context based on the session status
  const isPositionInterview =
    session.status === CvEditorSessionStatus.INTERVIEW_POSITIONS_IN_PROGRESS;
  const isAboutMeInterview =
    session.status === CvEditorSessionStatus.INTERVIEW_ABOUT_ME_IN_PROGRESS;

  // Select the correct chat history and other relevant data based on the context
  const chatHistory = isPositionInterview
    ? session.positionsChatHistory
    : session.aboutMeChatHistory;
  const currentPosition =
    isPositionInterview && typeof session.currentPositionIndex === "number"
      ? session.parsedCvData?.sections?.experience?.items?.[
          session.currentPositionIndex
        ]
      : null;
  const totalPositions =
    session.parsedCvData?.sections?.experience?.items?.length || 0;
  const isLastPosition =
    isPositionInterview && session.currentPositionIndex === totalPositions - 1;

  // --- API MUTATIONS ---
  // These mutations call our API service and update the parent state on success.

  const handleMutationSuccess = (updatedSession: CvEditorSession) => {
    dispatch({ type: "SET_SESSION", payload: updatedSession });
  };

  const handleMutationError = (error: any) => {
    dispatch({
      type: "SET_ERROR",
      payload: error.message || t("errors.api.interviewFailed"),
    });
  };

  const textReplyMutation = useMutation(
    (text: string) => sendInterviewTextReply(session.id, text),
    { onSuccess: handleMutationSuccess, onError: handleMutationError }
  );

  const audioReplyMutation = useMutation(
    (data: { audioBlob: Blob; duration: number }) =>
      sendInterviewAudioReply(session.id, data.audioBlob, data.duration),
    { onSuccess: handleMutationSuccess, onError: handleMutationError }
  );

  const nextStepMutation = useMutation(
    () => moveToNextInterviewStep(session.id),
    {
      // We don't need a long delay, just a quick loading indicator from the parent
      // which is handled by passing the isLoading prop.
      onSuccess: (updatedSession) => {
        // --- THIS IS THE FIX ---

        // 1. First, update the session with the new status from the backend.
        // This will be INTERVIEW_LOADING or FINAL_GENERATION.
        dispatch({ type: "SET_SESSION", payload: updatedSession });

        // 2. NOW, check if this new status requires polling.
        const pollingStatuses = [
          CvEditorSessionStatus.INTERVIEW_LOADING_ABOUT_ME,
          CvEditorSessionStatus.FINAL_GENERATION, // For the final summarization
        ];

        if (pollingStatuses.includes(updatedSession.status)) {
          // 3. If so, explicitly tell the parent modal to start polling.
          dispatch({ type: "SET_POLLING", payload: true });
        }

        // --- END OF FIX ---
      },
      onError: handleMutationError,
    }
  );

  const skipReplyMutation = useMutation(
    () => sendInterviewSkipReply(session.id),
    { onSuccess: handleMutationSuccess, onError: handleMutationError }
  );

  const isThinking =
    textReplyMutation.isLoading ||
    audioReplyMutation.isLoading ||
    nextStepMutation.isLoading ||
    skipReplyMutation.isLoading;

  // Determine if the user has replied to the latest AI question, enabling the "Next" button.
  const canMoveToNext = useMemo(() => {
    // Filter messages for the current context (current position or about me)
    const relevantMessages = chatHistory.filter((msg) =>
      isPositionInterview
        ? msg.positionIndex === session.currentPositionIndex
        : true
    );
    // Check if there is at least one USER message that was NOT a skip
    return relevantMessages.some(
      (msg) =>
        msg.actor === CvMessageActor.USER &&
        (msg.content as any).type !== UserInputType.SKIP
    );
  }, [chatHistory, session.currentPositionIndex, isPositionInterview]);

  // --- RENDER LOGIC ---

  const renderHeader = () => {
    let title = t("interview.aboutMe.title");
    let subtitle = t("interview.aboutMe.subtitle");

    if (
      isPositionInterview &&
      currentPosition &&
      typeof session.currentPositionIndex === "number"
    ) {
      title = t("interview.position.title", {
        index: session.currentPositionIndex + 1,
        total: totalPositions,
      });
      subtitle = `${currentPosition.position} at ${currentPosition.company}`;
    }

    return (
      <div className="flex-shrink-0 px-6 py-2 border-b border-gray-200 bg-white">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <AudioUsageTracker
        sessionUsed={session.sessionAudioSecondsUsed || 0}
        lifetimeRemaining={session.lifetimeAudioSecondsRemaining || 0}
      />
      {renderHeader()}

      <div className="flex-grow overflow-y-auto min-h-0">
        <ChatHistory
          session={session}
          chatHistory={chatHistory}
          isThinking={isThinking}
        />
      </div>

      <div className="flex-shrink-0 bg-white border-t border-gray-200">
        <InputArea
          // Pass down mutation functions and state
          onSendText={textReplyMutation.mutate}
          onSendAudio={audioReplyMutation.mutate}
          onNextStep={nextStepMutation.mutate}
          onSkip={skipReplyMutation.mutate}
          isThinking={isThinking}
          // Control the "Next" button's state from here
          canMoveToNext={canMoveToNext}
          // Let the InputArea know what the final "Next" button should say
          isLastStep={isAboutMeInterview || isLastPosition}
        />
      </div>
    </div>
  );
};
